import React, { useMemo, useState } from 'react';
import { Form, Input, Card, Space, Button, Checkbox, Descriptions, Select, Switch } from 'antd';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { DeviceRequest, TagsResponse } from '@openapi';

import api from '@services/api';
import { Device, DeviceTypeSettings } from '@services/types';
import { Navigation } from '@constants/Navigation';
import { AppContext } from '@layouts/AppRouter';
import { selectSearchFilter } from '@services/utils';

const DeviceUpdatePage: React.FC = () => {
  const [device, setDevice] = React.useState<Device>();
  const [deviceTypeSettings, setDeviceTypeSettings] = React.useState<DeviceTypeSettings>();
  const [tagsArray, setTagsArray] = useState<any>({
    options: [],
    activeOptions: [],
    loading: true,
  });
  const { settings } = React.useContext(AppContext);
  const { id: deviceId }: { id: string } = useParams();
  const history = useHistory();
  const [form] = Form.useForm<DeviceRequest>();

  React.useEffect(() => {
    Promise.all([api.devices.getDevice(deviceId), api.tags.getTags(deviceId)])
      .then(([device, tags]) => {
        form.setFieldsValue(device);
        setDevice(device);
        setTagsArray({
          options: [...Array.from(tags), ...device.pendingTags].map((item: any) => ({
            ...item,
            label: item.command,
            value: item.id,
          })),
          activeOptions: device.pendingTags.map((item) => ({ ...item, label: item.command, value: item.id })),
        });
      })
      .finally(() => {
        setTagsArray((prevState: any) => ({
          ...prevState,
          loading: false,
        }));
      });
  }, [form, deviceId]);

  React.useEffect(() => {
    if (device?.deviceTypeId) {
      api.administration.getDeviceTypeSettings(device.deviceTypeId).then(setDeviceTypeSettings);
    }
  }, [device]);

  const devicesOptions = useMemo(() => {
    let filteredTags = tagsArray.options;
    const locking = tagsArray.activeOptions.map((item: any) => item.command);
    if (locking.includes('locking')) {
      filteredTags = filteredTags.filter((item: any) => item.command !== 'unlocking');
    }
    if (locking.includes('unlocking')) {
      filteredTags = filteredTags.filter((item: any) => item.command !== 'locking');
    }
    return filteredTags;
  }, [tagsArray]);

  const handleClose = () => {
    history.push(Navigation.Devices);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await api.devices.updateDevice(deviceId, values);
      toast.success('Device was updated');
      form.resetFields();
      handleClose();
    } catch (error) {
      toast.error('Device was not updated');
      console.log('Device update ~ handleSubmit ~ error', error);
    }
  };

  const reinitializeTryCounter = async () => {
    try {
      api.devices.reinitializeTryCounter(deviceId);
      toast.success('Device try counter was reinitialized');
    } catch (error) {
      toast.error('Device try counter was not reinitialized');
      console.log('Device try counter reinitialize ~ error', error);
    }
  };
  const handleSelectChange = (select: any, options: any) => {
    setTagsArray((prevState: any) => ({
      ...prevState,
      activeOptions: options,
    }));
  };

  return (
    <Card
      title="Device Update"
      extra={
        <Space>
          <Button type="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={reinitializeTryCounter}>
            Reinitialize try counter
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Save
          </Button>
        </Space>
      }
    >
      <Descriptions layout="horizontal" column={2}>
        <Descriptions.Item label="Id">{device?.id}</Descriptions.Item>
        <Descriptions.Item label="Device Type">{device?.etc.deviceTypeName}</Descriptions.Item>
        <Descriptions.Item label="STB group">{device?.etc.groupName}</Descriptions.Item>
        <Descriptions.Item label="Mac Address">{device?.macAddress}</Descriptions.Item>
        <Descriptions.Item label="Serial Number">{device?.serialNumber}</Descriptions.Item>
        <Descriptions.Item label="Firmware Version">{device?.firmwareVersion}</Descriptions.Item>
        <Descriptions.Item label="Loader Version">{device?.loaderVersion}</Descriptions.Item>
        <Descriptions.Item label="UI Version">{device?.uiVersion}</Descriptions.Item>
        {settings?.enableTags && (
          <Descriptions.Item label="Tags">
            {`${device?.isLocked ? 'Locked' : 'Unlocked'} ${
              device?.pendingTags && device?.pendingTags?.length > 0 ? ',' : '.'
            } ${device?.pendingTags.map((item) => item.command).join(', ')}`}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Form form={form} layout="vertical" name="form_in_modal">
        <Form.Item
          name="comment"
          label="Comment"
          rules={[
            {
              max: 64,
              message: 'Comment must be at most 64 characters',
            },
          ]}
        >
          <Input />
        </Form.Item>
        {settings?.enableTags && !tagsArray.loading && (
          <Form.Item name="tagsIds" label="Tags:">
            <Select
              style={{ width: '20%' }}
              defaultValue={tagsArray.activeOptions}
              mode="multiple"
              value={tagsArray.activeOptions}
              onChange={handleSelectChange}
              filterOption={selectSearchFilter}
              allowClear
              options={devicesOptions}
              showSearch
            />
          </Form.Item>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 20%)', alignItems: 'center' }}>
          {settings?.enableDeviceHistory && (
            <Form.Item name="isLogHistoryAttributeSet" label="Log history:" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}
          {deviceTypeSettings?.urlPreProd && (
            <Form.Item name="isMoveToPreproductionAttributeSet" label="Move To Preproduction:" valuePropName="checked">
              <Checkbox />
            </Form.Item>
          )}
          {settings?.enableStbCommands && (
            <>
              <Form.Item name="isFullResetAttributeSet" label="Full Reset:" valuePropName="checked">
                <Checkbox />
              </Form.Item>
              <Form.Item name="isLightResetAttributeSet" label="Light Reset:" valuePropName="checked">
                <Checkbox />
              </Form.Item>
              <Form.Item name="isTraceEnabledAttributeSet" label="Trace Enabled:" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default React.memo(DeviceUpdatePage);
